import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react'
import { useRouter } from 'next/router'
import { SiteBuilderContext } from 'src/siteBuilder/renderer/context/SiteBuilderContext'
import { getOffers, isBusinessPage } from '../Helpers'
import type { BestOffer } from '../pepMemoryAndColorSection/pepMemoryAndColorSection'

const CONSUMER_ACCOUNT_TYPE = { accountType: 'I', accountSubType: 'R' }
const BUSINESS_ACCOUNT_TYPE = { accountType: 'B', accountSubType: 'P' }

const getAccountTypeBody = (isBusiness: boolean) => (isBusiness ? BUSINESS_ACCOUNT_TYPE : CONSUMER_ACCOUNT_TYPE)

interface OfferPrice {
  type: string
  amount?: number
}

interface OffersResponse {
  productOfferings: Array<{
    dependencies: {
      included: Array<{
        offerings: Array<{
          prices: OfferPrice[]
          specification: Array<{
            attributes: Array<{ property: string; value: string }>
          }>
        }>
      }>
    }
  }>
}

const mapOffersToPricing = (offers: OffersResponse): DevicePricing => {
  if (!Array.isArray(offers?.productOfferings) || offers.productOfferings.length === 0) {
    throw new Error('No product offerings found')
  }

  const firstOffering = offers.productOfferings[0]?.dependencies?.included?.[0]?.offerings?.[0]

  const isBibEligible =
    firstOffering?.specification?.[0]?.attributes?.find((attr) => attr.property === 'bibEligible')?.value === 'true'

  const defaultPricing: DevicePricing = {
    monthlyPrice: 0,
    bibMonthlyPrice: undefined,
    basePrice: 0,
    bibBasePrice: undefined,
    retailBasePrice: undefined,
    isBibEligible,
  }

  const prices = firstOffering?.prices
  if (!Array.isArray(prices)) return defaultPricing

  return prices.reduce((acc, price) => {
    switch (price.type) {
      case 'MONTHLY_INSTALLMENT':
        acc.monthlyPrice = price.amount ?? 0
        break
      case 'BIB_MONTHLY_INSTALLMENT':
        acc.bibMonthlyPrice = price.amount
        break
      case 'DISCOUNTED_PRICE':
        acc.basePrice = price.amount ?? 0
        break
      case 'BIB_DISCOUNTED_PRICE':
        acc.bibBasePrice = price.amount
        break
      case 'BASE_PRICE':
        acc.retailBasePrice = price.amount
        break
    }
    return acc
  }, defaultPricing)
}

export interface DevicePricing {
  monthlyPrice?: number
  bibMonthlyPrice?: number
  basePrice?: number
  bibBasePrice?: number
  retailBasePrice?: number
  isBibEligible: boolean
}

export interface StickyBarText {
  ctaText?: string
  ctaLink?: string
  preOrderCTAText?: string
  preOrderCTALink?: string
  legalCalloutBanner?: string
  monthlyPriceText?: string
  upfrontPriceText?: string
  monthlyBottomText?: string
  upfrontBottomText?: string
  monthlyLegalText?: string
  upfrontLegalText?: string
  priceTextSize?: string
  priceSignDirection?: string
  stickyPriceBarEnabled?: boolean
  priceSectionEnabled?: boolean
  disabledPriceText?: string
  errorCardMessage?: string
  errorButtonText?: string
}

export interface PepPricingState {
  pricing: DevicePricing | null
  isLoading: boolean
  error: string | null
  bibEnabled: boolean
  setBibEnabled: (value: boolean | ((prev: boolean) => boolean)) => void
  bibActive: boolean
  displayMonthly: number
  displayUpfront: number
  stickyBarText: StickyBarText
  setStickyBarText: (text: StickyBarText) => void
  sku: string
  productIdOrSlug: string
  isPreOrder: boolean
  setVariantSku: (productCode: string | undefined, isPreOrder?: boolean, bestOffer?: BestOffer) => void
  /** Signals that the colour picker has settled (success, error, or absent) and pricing
   *  may proceed. Accepts the resolved variant SKU, or undefined to fall back to baseSku, and the
   *  variant's bestOffer override when one is set. */
  onVariantResolved: (productCode?: string, bestOffer?: BestOffer) => void
  planId?: string
  userSelectedPlan: boolean
}

const usePepPricing = (defaultProductId?: string, enabled = true): PepPricingState => {
  const router = useRouter()
  const { locale } = useContext(SiteBuilderContext)
  const baseSku = defaultProductId ?? (router.query.mobilityProductId as string | undefined) ?? ''
  const planId = typeof router.query.planId === 'string' ? router.query.planId : undefined
  const userSelectedPlan = router.query.userSelectedPlan === 'true'
  const { accountType, accountSubType } = getAccountTypeBody(isBusinessPage(router.asPath))

  const [pricing, setPricing] = useState<DevicePricing | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [bibEnabled, setBibEnabled] = useState(true)
  const [stickyBarText, setStickyBarText] = useState<StickyBarText>({})
  const [skuOverride, setSkuOverride] = useState<string | undefined>(undefined)
  const [isPreOrder, setIsPreOrder] = useState(false)
  // Manual price from Products & Services for the selected variant. When set it replaces the
  // offers API result, as in the legacy mobility-pep app.
  const [bestOffer, setBestOffer] = useState<BestOffer | undefined>(undefined)
  // True once the colour picker has signalled it has settled (via onVariantResolved).
  // Keeps isLoading=true until we know which SKU to fetch — without this, skuOverride's
  // truthiness would carry both "which SKU" and "has the picker decided yet", which fall
  // apart when the picker resolves with no productCode or when defaultProductId is absent.
  const [variantResolved, setVariantResolved] = useState(false)

  // Resets the override when the base SKU genuinely changes. Compares the previous value
  // rather than skipping the first invocation: effects run children-first within a commit,
  // so a picker that resolves synchronously in its own mount effect (e.g. no slug to fetch
  // against) would otherwise have its onVariantResolved call clobbered by this effect firing
  // immediately after — and under StrictMode's double-invoked mount effects, a
  // skip-the-first-run guard is defeated on the second pass.
  const prevBaseSku = useRef(baseSku)

  useEffect(() => {
    if (prevBaseSku.current === baseSku) return
    prevBaseSku.current = baseSku
    setSkuOverride(undefined)
    setIsPreOrder(false)
    setBestOffer(undefined)
    setVariantResolved(false)
  }, [baseSku])

  const sku = skuOverride || baseSku

  const setVariantSku = useCallback((productCode: string | undefined, preOrder?: boolean, offer?: BestOffer) => {
    setSkuOverride(productCode)
    setIsPreOrder(!!preOrder)
    setBestOffer(offer)
  }, [])

  // Signals that the picker has settled. Sets skuOverride (undefined → fall back to baseSku)
  // and marks variantResolved so the fetch gate opens on the next render. The bestOffer is set in
  // the same batch so an override never flashes the API price first.
  const onVariantResolved = useCallback((productCode?: string, offer?: BestOffer) => {
    setSkuOverride(productCode)
    setBestOffer(offer)
    setVariantResolved(true)
  }, [])

  const bestOfferMonthly = bestOffer?.financeInstallmentAmount
  const bestOfferPrice = bestOffer?.price

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false)
      return
    }

    if (!sku) {
      setIsLoading(false)
      setError('Unable to determine product for pricing')
      return
    }

    const province = locale.province?.toUpperCase()
    if (!province) {
      setIsLoading(false)
      setError('Unable to determine province for pricing')
      return
    }

    // Intentionally leaves isLoading true: the picker hasn't resolved a SKU yet,
    // so we stay in the loading state rather than flashing $0.
    if (!variantResolved) {
      return
    }

    // Legacy mobility-pep rule: a bestOffer financeInstallmentAmount wins over the offers API (no
    // request is made). BIB is off and the retail line is hidden since the override carries neither.
    if (bestOfferMonthly != null) {
      setPricing({
        monthlyPrice: bestOfferMonthly,
        basePrice: bestOfferPrice ?? 0,
        isBibEligible: false,
      })
      setError(null)
      setIsLoading(false)
      return
    }

    let cancelled = false

    const fetchPricing = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const { offers } = await getOffers({
          mobilityProductId: sku,
          accountType,
          accountSubtype: accountSubType,
          province,
        })

        if (cancelled) return
        setPricing(mapOffersToPricing(offers))
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Something went wrong')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void fetchPricing()

    return () => {
      cancelled = true
    }
  }, [sku, enabled, accountType, accountSubType, locale.province, variantResolved, bestOfferMonthly, bestOfferPrice])

  const bibActive = bibEnabled && !!pricing?.isBibEligible
  const displayMonthly = bibActive ? pricing?.bibMonthlyPrice ?? pricing?.monthlyPrice ?? 0 : pricing?.monthlyPrice ?? 0
  const displayUpfront = bibActive ? pricing?.bibBasePrice ?? pricing?.basePrice ?? 0 : pricing?.basePrice ?? 0

  return {
    pricing,
    isLoading,
    error,
    bibEnabled,
    setBibEnabled,
    bibActive,
    displayMonthly,
    displayUpfront,
    stickyBarText,
    setStickyBarText,
    sku,
    productIdOrSlug: '',
    isPreOrder,
    setVariantSku,
    onVariantResolved,
    planId,
    userSelectedPlan,
  }
}

export default usePepPricing

const defaultState: PepPricingState = {
  pricing: null,
  isLoading: false,
  error: null,
  bibEnabled: false,
  setBibEnabled: () => undefined,
  bibActive: false,
  displayMonthly: 0,
  displayUpfront: 0,
  stickyBarText: {},
  setStickyBarText: () => undefined,
  sku: '',
  productIdOrSlug: '',
  isPreOrder: false,
  setVariantSku: () => undefined,
  onVariantResolved: () => undefined,
  planId: undefined,
  userSelectedPlan: false,
}

export const PepPricingContext = createContext<PepPricingState>(defaultState)

export const usePepPricingContext = () => useContext(PepPricingContext)

export const formatPrice = (amount: number, localeTag: string) =>
  amount.toLocaleString(localeTag, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export const getLocaleTag = (language?: string) => `${language ?? 'en'}-CA`

export const setBIBToggleAnalyticEvent = ({ isToggleEnabled, URL }: { isToggleEnabled: boolean; URL: string }) => {
  if (!window.adobeDataLayer) window.adobeDataLayer = []

  window.adobeDataLayer.push({
    event: 'toolSubmit',
    eventInfo: 'Tool Interaction Submit Event',
    xdm: {
      web: {
        webInteraction: {
          name: `bib_toggle_${isToggleEnabled ? 'enabled' : 'disabled'}`,
          type: 'other',
          URL,
          _telus: {
            toolUsageSubmitted: 1,
            toolName: 'mobility-pep:bib_toggle',
            toolType: 'bib_toggle',
          },
        },
      },
    },
  })
}
